import React, {useEffect, useState} from 'react';
import {CustomerMessage} from '../../CustomerMessage';
import useCustomerRead from './useCustomerRead';

export const displayDate = value => value ? new Date(value).toLocaleString() : 'Not recorded';

export function ReadError({error, retry, label}) {
  return <div role="alert" className="cd-feedback">
    <p>{error}</p><button type="button" onClick={retry}>Retry {label}</button>
  </div>;
}

function DirectoryMessage({id}) {
  const resource = useCustomerRead(`/account/messages/message/${encodeURIComponent(id)}?isApi=true`,
    {api: false});
  const [revealed, setRevealed] = useState(false);
  const [linksEnabled, setLinksEnabled] = useState(false);
  if (resource.loading) return <li role="status">Loading message…</li>;
  if (resource.error) return <li><ReadError {...resource} label="message" /></li>;
  return <CustomerMessage
    message={resource.data?.message}
    isRevealed={revealed}
    linksEnabled={linksEnabled}
    onReveal={() => setRevealed(true)}
    onEnableLinks={() => setLinksEnabled(true)}
    reportUrl={`/account/messages/report?messageId=${encodeURIComponent(id)}`}
  />;
}

function ActivityEvent({event}) {
  const {data, type} = event;
  if (type === 'message' || type === 'private-share') {
    return <li className="cd-event">
      <p><strong>{type === 'private-share' ? 'Private file share'
        : data.kind === 'thank-you' ? 'Thank-you email'
          : data.direction === 'inbound' ? 'Customer message' : 'Outgoing message'}</strong></p>
      {data.privateShare && <p>
        {data.privateShare.revokedAt ? 'Rescinded' : 'Shared'} · Expires: {displayDate(data.privateShare.expiresAt)}
      </p>}
      <ul className="cd-messages"><DirectoryMessage id={data.id} /></ul>
    </li>;
  }
  return <li className="cd-event">
    <p><strong>{type === 'order' ? `Order · ${data.productName || 'Product'}`
      : type === 'review' ? (data.direction === -1 ? 'Review of customer' : 'Review from customer')
        : type === 'contact-block' ? (data.action === 'blocked' ? 'Contact blocked' : 'Contact block rescinded')
          : 'Customer activity'}</strong></p>
    <p>{displayDate(event.at)}</p>
    {type === 'order' && <p>{data.status} · {data.isRefunded ? 'Refunded'
      : data.isCanceled ? 'Canceled' : data.isPurchased ? 'Purchased' : 'Unpaid'}</p>}
    {type === 'review' && <p>Rating: {data.rating} · {data.comment}</p>}
  </li>;
}

export default function CustomerActivity({customerId}) {
  const [cursor, setCursor] = useState('');
  const [events, setEvents] = useState([]);
  const resource = useCustomerRead(`/v1/seller/customers/${customerId}/activity?limit=25${
    cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`);
  useEffect(() => {
    if (!resource.data) return;
    setEvents(previous => {
      const merged = new Map((cursor ? previous : []).map(event => [`${event.type}:${event.id}`, event]));
      resource.data.data.forEach(event => merged.set(`${event.type}:${event.id}`, event));
      return [...merged.values()];
    });
  }, [resource.data, cursor]);
  return <div>
    {resource.loading && <p role="status">Loading activity…</p>}
    {resource.error && <ReadError {...resource} label="activity" />}
    <ul className="cd-timeline">{events.map(event =>
      <ActivityEvent key={`${event.type}:${event.id}`} event={event} />)}</ul>
    {!resource.loading && !resource.error && !events.length && <p>No activity yet.</p>}
    {resource.data?.meta?.hasMore && <button type="button" disabled={resource.loading}
      onClick={() => setCursor(resource.data.meta.nextCursor)}>Load more activity</button>}
  </div>;
}
